/**
 * MiniMax API Service
 * 文档: https://platform.minimax.io/docs/api-reference/voice-cloning-clone
 */

export interface UploadAudioFileResponse {
  file_id: string
}

export interface CloneVoiceParams {
  fileId: string // MiniMax voice_clone API 是无状态的，每次调用都需要 file_id
  voiceId: string
  apiKey: string
  baseUrl?: string
  previewText?: string
  model?: string
  promptAudioFileId?: string
  promptText?: string
}

export interface CloneVoiceResponse {
  voiceId: string
  previewUrl?: string
}

export interface GenerateSpeechParams {
  text: string
  runtimeVoiceId: string
  fileId: string // stateless API 需要原始音频 file_id
  apiKey: string
  model: string
  baseUrl?: string
}

const DEFAULT_BASE_URL = 'https://api.minimax.io'

/**
 * 上传音频文件到MiniMax
 * @param file 音频文件
 *   - 格式：mp3/m4a/wav
 *   - 时长：最少10秒，最多5分钟
 *   - 大小：最多20MB
 * @param apiKey MiniMax API Key
 * @param baseUrl API基础URL
 * @returns file_id 用于语音克隆的文件ID
 */
export async function uploadAudioFile(
  file: File,
  apiKey: string,
  baseUrl: string = DEFAULT_BASE_URL,
): Promise<string> {
  // 验证文件
  const validExtensions = ['mp3', 'm4a', 'wav']
  const fileExtension = file.name.split('.').pop()?.toLowerCase()

  if (!fileExtension || !validExtensions.includes(fileExtension)) {
    throw new Error(`不支持的音频格式。支持的格式: ${validExtensions.join(', ')}`)
  }

  if (file.size > 20 * 1024 * 1024) {
    throw new Error('文件大小不能超过20MB')
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('purpose', 'voice_clone')

  // 标准化 baseUrl，移除尾部斜杠和可能的 /v1
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '').replace(/\/v1$/, '')
  const uploadUrl = `${normalizedBaseUrl}/v1/files/upload`

  console.info('📤 上传音频文件:', {
    url: uploadUrl,
    fileName: file.name,
    fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
    hasApiKey: !!apiKey,
    apiKeyPrefix: apiKey ? `${apiKey.substring(0, 10)}...` : 'undefined',
  })

  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('上传失败:', errorText)

    let errorData
    try {
      errorData = JSON.parse(errorText)
    }
    catch {
      errorData = {}
    }

    throw new Error(
      errorData.message || errorData.base_resp?.status_msg || `文件上传失败: ${response.status} ${response.statusText}`,
    )
  }

  const data = await response.json()

  // 先检查业务状态码
  if (data.base_resp && data.base_resp.status_code !== 0) {
    const statusCode = data.base_resp.status_code
    const statusMsg = data.base_resp.status_msg || '未知错误'

    console.error('文件上传业务错误:', { statusCode, statusMsg, fullResponse: data })

    const errorMessages: Record<number, string> = {
      1008: '余额不足，请充值后再试',
      1004: '请求过于频繁，请稍后再试',
      1002: 'API Key 无效',
      2002: '文件内容包含敏感信息',
      2013: '参数错误',
    }

    const friendlyMessage = errorMessages[statusCode] || statusMsg
    throw new Error(`文件上传失败: ${friendlyMessage} (错误码: ${statusCode})`)
  }

  // MiniMax API 可能返回不同的结构，需要兼容处理
  // 可能的格式：
  // 1. { file: { file_id: "xxx" } }
  // 2. { file_id: "xxx" }
  // 3. { data: { file_id: "xxx" } }
  const rawFileId = data.file?.file_id || data.file_id || data.data?.file_id

  if (!rawFileId) {
    console.error('无法从响应中获取 file_id，完整响应:', data)
    throw new Error('上传成功但未返回 file_id，请检查 API 配置')
  }

  return String(rawFileId)
}

/**
 * 克隆声音 / 使用已克隆的声音生成音频
 * @param params 克隆参数
 *   - fileId: 源音频的 file_id（必需，API 为无状态，需要每次携带）
 *   - voiceId: 每次请求的自定义 ID（需保证唯一，避免 voice_id duplicate 错误）
 *   - apiKey: MiniMax API Key（必需）
 *   - baseUrl: API基础URL（可选）
 *   - previewText: 生成预览音频的文本（可选）
 *   - model: 使用的模型，默认 speech-2.6-hd（可选）
 *   - promptAudioFileId: 示例音频的 file_id（可选，提升克隆质量）
 *   - promptText: 示例音频的文本（可选，与 promptAudioFileId 配合使用）
 * @returns 克隆结果（包含voiceId和预览音频URL）
 */
export async function cloneVoice(params: CloneVoiceParams): Promise<CloneVoiceResponse> {
  const {
    fileId,
    voiceId,
    apiKey,
    baseUrl = DEFAULT_BASE_URL,
    previewText,
    model = 'speech-2.6-hd',
    promptAudioFileId,
    promptText,
  } = params

  if (!fileId) {
    throw new Error('调用 voice_clone 需要 file_id')
  }

  const requestBody: any = {
    voice_id: voiceId,
    file_id: Number.isNaN(Number(fileId)) ? fileId : Number(fileId),
  }

  // 如果提供了示例音频（prompt_audio），添加到请求中
  if (promptAudioFileId && promptText) {
    requestBody.clone_prompt = {
      prompt_audio: promptAudioFileId,
      prompt_text: promptText,
    }
  }

  // 如果提供了预览文本，添加到请求中以生成预览音频
  if (previewText) {
    requestBody.text = previewText
    requestBody.model = model
  }

  // 标准化 baseUrl，移除尾部斜杠和可能的 /v1
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '').replace(/\/v1$/, '')
  const cloneUrl = `${normalizedBaseUrl}/v1/voice_clone`

  const response = await fetch(cloneUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('❌ 克隆失败响应:', errorText)

    let errorData
    try {
      errorData = JSON.parse(errorText)
    }
    catch {
      errorData = {}
    }

    throw new Error(
      errorData.base_resp?.status_msg || errorData.message || `声音克隆失败: ${response.status} ${response.statusText}`,
    )
  }

  const data = await response.json()

  // 检查业务状态码
  if (data.base_resp && data.base_resp.status_code !== 0) {
    const statusCode = data.base_resp.status_code
    const statusMsg = data.base_resp.status_msg || '未知错误'

    console.error('❌ 业务错误:', { statusCode, statusMsg })

    // 根据错误码提供友好提示
    const errorMessages: Record<number, string> = {
      1008: '余额不足，请充值后再试',
      1004: '请求过于频繁，请稍后再试',
      1002: 'API Key 无效',
      2002: '文本内容包含敏感信息',
      2013: '参数错误',
    }

    const friendlyMessage = errorMessages[statusCode] || statusMsg
    throw new Error(`${friendlyMessage} (错误码: ${statusCode})`)
  }

  // 检查内容安全
  if (data.input_sensitive) {
    throw new Error('音频内容包含敏感信息，无法克隆')
  }

  return {
    voiceId,
    previewUrl: data.demo_audio || undefined,
  }
}

/**
 * 生成语音（调用 voice_clone 生成预览音频）
 * @param params 生成参数
 *   - text: 要转换的文本
 *   - runtimeVoiceId: 本次调用的临时 voice_id（需保证唯一）
 *   - fileId: 上传音频得到的 file_id
 *   - apiKey: MiniMax API Key
 *   - model: 使用的模型
 *   - baseUrl: API 基础 URL
 * @returns 音频数据 (ArrayBuffer)
 */
export async function generateSpeech(params: GenerateSpeechParams): Promise<ArrayBuffer> {
  const {
    text,
    runtimeVoiceId,
    fileId,
    apiKey,
    model,
    baseUrl = DEFAULT_BASE_URL,
  } = params

  if (!text || text.trim().length === 0) {
    console.warn('⚠️ 文本为空，跳过语音生成')
    throw new Error('文本内容为空')
  }
  if (!runtimeVoiceId) {
    throw new Error('runtimeVoiceId 不能为空')
  }
  if (!fileId) {
    throw new Error('file_id 不能为空')
  }

  const result = await cloneVoice({
    fileId,
    voiceId: runtimeVoiceId,
    apiKey,
    baseUrl,
    previewText: text.trim(),
    model,
  })

  if (!result.previewUrl) {
    throw new Error('生成成功但未返回音频URL')
  }

  const audioResponse = await fetch(result.previewUrl)
  if (!audioResponse.ok) {
    throw new Error('音频下载失败')
  }

  return await audioResponse.arrayBuffer()
}

/**
 * T2A V2 语音合成
 * 使用持久化的 voice_id 直接调用 /v1/t2a_v2，支持 emotion 参数
 */
export interface GenerateSpeechT2AParams {
  text: string
  voiceId: string
  apiKey: string
  model?: string
  baseUrl?: string
  emotion?: string
  speed?: number
  format?: string
}

export async function generateSpeechT2A(params: GenerateSpeechT2AParams): Promise<ArrayBuffer> {
  const {
    text,
    voiceId,
    apiKey,
    model = 'speech-2.8-hd',
    baseUrl = DEFAULT_BASE_URL,
    emotion,
    speed = 1.0,
    format = 'mp3',
  } = params

  if (!text?.trim())
    throw new Error('文本内容为空')
  if (!voiceId)
    throw new Error('voice_id 不能为空')

  const requestBody: Record<string, unknown> = {
    model,
    text: text.trim(),
    stream: false,
    voice_setting: {
      voice_id: voiceId,
      speed,
    },
    audio_setting: {
      format,
      sample_rate: 32000,
    },
  }

  if (emotion) {
    requestBody.voice_setting = {
      ...(requestBody.voice_setting as Record<string, unknown>),
      emotion,
    }
  }

  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '').replace(/\/v1$/, '')
  const url = `${normalizedBaseUrl}/v1/t2a_v2`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorData: Record<string, unknown> = {}
    try {
      errorData = JSON.parse(errorText)
    }
    catch {}

    if (response.status === 401)
      throw new Error('API Key 无效')
    if (response.status === 402)
      throw new Error('余额不足，请充值后再试')
    const statusMsg = typeof (errorData as Record<string, unknown>).base_resp === 'object'
      ? ((errorData as Record<string, unknown>).base_resp as Record<string, unknown>)?.status_msg
      : undefined
    throw new Error((statusMsg as string) || `T2A V2 语音合成失败: ${response.status}`)
  }

  const data = await response.json()

  if (data.base_resp && data.base_resp.status_code !== 0) {
    throw new Error(data.base_resp.status_msg || `T2A V2 错误: ${data.base_resp.status_code}`)
  }

  if (!data.data?.audio) {
    throw new Error('T2A V2 未返回音频数据')
  }

  // T2A V2 返回 hex 编码的音频（如 "4944330400..."）
  const hexString = data.data.audio as string
  const bytes = new Uint8Array(hexString.length / 2)
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(hexString.slice(i * 2, i * 2 + 2), 16)
  }
  return bytes.buffer
}
